import {
  Children,
  Fragment,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { OptionFilter, type Option } from './pages/tenant/OptionFilter';

/** Legge anche frammenti e gruppi, mantenendo etichette, valori e voci disabilitate. */
function choices(
  children: ReactNode,
  group = '',
  disabled = false,
): Array<Option & { disabled: boolean }> {
  return Children.toArray(children).flatMap((node) => {
    if (
      !isValidElement<{
        children?: ReactNode;
        value?: string | number;
        label?: string;
        disabled?: boolean;
      }>(node)
    )
      return [];
    const p = node.props;
    if (node.type === Fragment) return choices(p.children, group, disabled);
    if (node.type === 'optgroup')
      return choices(p.children, p.label ?? '', disabled || !!p.disabled);
    if (node.type !== 'option') return [];
    const text = (content: ReactNode): string =>
      Children.toArray(content)
        .map((c) =>
          isValidElement<{ children?: ReactNode }>(c) ? text(c.props.children) : String(c),
        )
        .join('');
    const label = p.label ?? text(p.children);
    return [
      {
        value: String(p.value ?? text(p.children)),
        label: group ? `${group} · ${label}` : label,
        disabled: disabled || !!p.disabled,
      },
    ];
  });
}

/** Mantiene un vero select per eventi, FormData, reset e validazione nativa del form. */
export function AdaptiveSelect({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  const native = useRef<HTMLSelectElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const [uncontrolled, setUncontrolled] = useState(props.defaultValue);
  const [invalid, setInvalid] = useState(false);
  const options = choices(children);
  const enabled = options.filter((o) => !o.disabled && o.value !== '');
  const long = new Set(enabled.map((o) => o.value)).size > 10;
  const current = props.value ?? uncontrolled;
  const selected = Array.isArray(current)
    ? current.map(String)
    : [String(current ?? options.find((o) => !o.disabled)?.value ?? '')];
  useEffect(() => {
    const form = native.current?.form;
    let timer: ReturnType<typeof setTimeout>;
    const reset = () => {
      timer = setTimeout(() => {
        const select = native.current;
        if (!select) return;
        setUncontrolled(
          select.multiple ? Array.from(select.selectedOptions, (o) => o.value) : select.value,
        );
        setInvalid(false);
      }, 0);
    };
    form?.addEventListener('reset', reset);
    return () => {
      clearTimeout(timer);
      form?.removeEventListener('reset', reset);
    };
  }, []);
  if (!props.multiple && !options.some((o) => o.value === selected[0]) && enabled[0])
    selected[0] = enabled[0].value;
  const update = (values: string[]) => {
    const select = native.current!;
    for (const option of select.options) option.selected = values.includes(option.value);
    setUncontrolled(props.multiple ? values : (values[0] ?? ''));
    setInvalid(false);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const select = (
    <select
      {...props}
      ref={native}
      id={long ? undefined : props.id}
      className={long ? 'hidden' : props.className}
      tabIndex={long ? -1 : props.tabIndex}
      aria-hidden={long || undefined}
      aria-label={long ? undefined : props['aria-label']}
      onChange={(e) => {
        setUncontrolled(
          props.multiple
            ? Array.from(e.currentTarget.selectedOptions, (o) => o.value)
            : e.currentTarget.value,
        );
        setInvalid(false);
        props.onChange?.(e);
      }}
      onInvalid={(e) => {
        props.onInvalid?.(e);
        if (long) {
          e.preventDefault();
          setInvalid(true);
          wrapper.current?.querySelector('input')?.focus();
        }
      }}
    >
      {children}
    </select>
  );
  if (!long) return select;
  return (
    <div ref={wrapper} className="min-w-0 flex-1">
      {props.multiple && selected.filter(Boolean).length > 0 && (
        <div className="mb-1 flex flex-wrap gap-1">
          {selected.filter(Boolean).map((value) => (
            <button
              key={value}
              type="button"
              disabled={props.disabled}
              className="rounded bg-black/5 px-2 py-1 text-xs disabled:opacity-50"
              aria-label={`Rimuovi ${options.find((o) => o.value === value)?.label ?? value}`}
              onClick={() => update(selected.filter((v) => v !== value))}
            >
              {options.find((o) => o.value === value)?.label ?? value} ×
            </button>
          ))}
        </div>
      )}
      <OptionFilter
        forceAutocomplete
        id={props.id ?? id}
        label={props['aria-label'] ?? props.title ?? 'Scegli una voce'}
        className={props.className}
        disabled={props.disabled}
        invalid={invalid || !!props['aria-invalid']}
        describedBy={
          [props['aria-describedby'], invalid ? `${id}-error` : ''].filter(Boolean).join(' ') ||
          undefined
        }
        value={props.multiple ? '' : (selected[0] ?? '')}
        selectedValues={props.multiple ? selected : undefined}
        options={enabled}
        emptyLabel={options.find((o) => o.value === '')?.label ?? 'Cerca e scegli…'}
        allowEmpty={!props.multiple && options.some((o) => !o.value && !o.disabled)}
        clearLabel={`Svuota ${props['aria-label'] ?? 'selezione'}`}
        onChange={(value) =>
          update(
            props.multiple
              ? selected.includes(value)
                ? selected.filter((v) => v !== value)
                : [...selected.filter(Boolean), value]
              : [value],
          )
        }
        onBlur={() => native.current?.dispatchEvent(new FocusEvent('focusout', { bubbles: true }))}
      />
      {select}
      {invalid && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-red-700">
          Scegli una voce dell’elenco.
        </p>
      )}
    </div>
  );
}
